import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const HeroSlider = () => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&h=800&fit=crop',
      title: t.hero.title1,
      description: t.hero.desc1,
      link: '/trading'
    },
    {
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&h=800&fit=crop',
      title: t.hero.title2,
      description: t.hero.desc2,
      link: '/insurance'
    },
    {
      image: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1920&h=800&fit=crop',
      title: t.hero.title3,
      description: t.hero.desc3,
      link: '/tourism'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="relative h-[600px] overflow-hidden">
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 z-10" />
          <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 z-20 flex items-center">
            <div className="max-w-7xl mx-auto px-6 w-full">
              <div className="max-w-2xl">
                <h1 className="text-5xl font-light text-white mb-4 leading-tight">
                  {slide.title}
                </h1>
                <p className="text-xl text-white/90 mb-8 leading-relaxed">
                  {slide.description}
                </p>
                <a 
                  href={slide.link}
                  className="inline-block px-8 py-3 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
                >
                  {t.hero.readMore}
                </a>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Buttons - Hidden on mobile, visible on tablet and up */}
      <button
        onClick={prevSlide}
        className="hidden md:block absolute left-6 top-1/2 -translate-y-1/2 z-30 p-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full transition-colors"
        aria-label="Previous slide"
      >
        <ChevronLeft className="text-white" size={24} />
      </button>
      <button
        onClick={nextSlide}
        className="hidden md:block absolute right-6 top-1/2 -translate-y-1/2 z-30 p-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full transition-colors"
        aria-label="Next slide"
      >
        <ChevronRight className="text-white" size={24} />
      </button>

      {/* Slide Indicators - Hidden on mobile */}
      <div className="hidden md:flex absolute bottom-20 left-0 right-0 z-40 justify-center gap-2 pointer-events-none">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-12 h-1 rounded-full transition-all pointer-events-auto ${
              index === currentSlide ? 'bg-white w-16' : 'bg-white/50'
            }`}
          />
        ))}
      </div>

      {/* Slide Tabs at Bottom - Hidden on mobile */}
      <div className="hidden md:block absolute bottom-0 left-0 right-0 z-30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-3 gap-0">
            {slides.map((slide, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`py-4 px-6 text-left border-t-4 transition-all ${
                  index === currentSlide
                    ? 'bg-white border-teal-600'
                    : 'bg-neutral-100/90 border-transparent hover:bg-white/80'
                }`}
              >
                <span className={`text-sm font-medium ${
                  index === currentSlide ? 'text-neutral-900' : 'text-neutral-600'
                }`}>
                  {slide.title.split(':')[0]}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSlider;
